import React, { useEffect, useMemo, useState } from 'react';
import { Settings, AlertTriangle, Package, TrendingUp, CheckCircle, AlertCircle, Gauge, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useTheme } from '../context/ThemeContext';

const IntelligentUnitPrediction = ({ unitName, predictionData, predictionMeta, onGetPrediction }) => {
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllOverdue, setShowAllOverdue] = useState(false);
  const [showAllChecksMade, setShowAllChecksMade] = useState(false);
  const [advisoryRequested, setAdvisoryRequested] = useState(false);
  const [actionWindowIndex, setActionWindowIndex] = useState(0);
  const [riskCarouselIndex, setRiskCarouselIndex] = useState(0);
  const [sequenceAnimatedIn, setSequenceAnimatedIn] = useState(false);
  const [activeAltIndex, setActiveAltIndex] = useState(-1);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { theme } = useTheme();
  const predictionStatus = String(predictionMeta?.status || '');

  useEffect(() => {
    if (!predictionData || predictionData.error || advisoryRequested) return;
    if (['queued', 'loading_advisory', 'retry_wait', 'error_transient', 'error_terminal'].includes(predictionStatus)) return;
    const level = (predictionData.detail_level || 'summary').toLowerCase();
    if (level === 'advisory' || level === 'indicators') return;
    if (typeof onGetPrediction === 'function') {
      onGetPrediction(unitName, false, 'advisory');
      setAdvisoryRequested(true);
    }
  }, [unitName, predictionData, advisoryRequested, onGetPrediction, predictionStatus]);

  const preventivePlan = useMemo(
    () => (Array.isArray(predictionData?.preventive_plan) ? predictionData.preventive_plan : []),
    [predictionData?.preventive_plan]
  );

  const actionsByWindow = useMemo(() => {
    const grouped = { now: [], 'this shift': [], 'next 7 days': [], other: [] };
    preventivePlan.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const key = (item.when || '').toLowerCase();
      if (key === 'now') grouped.now.push(item);
      else if (key === 'this shift') grouped['this shift'].push(item);
      else if (key === 'next 7 days') grouped['next 7 days'].push(item);
      else grouped.other.push(item);
    });
    return grouped;
  }, [preventivePlan]);

  const getRiskColor = (riskLevel) => {
    const level = riskLevel?.toString() || '';
    switch (level.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-50 border-red-200 text-red-800';
      case 'High': return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'Medium': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'Low': return 'bg-green-50 border-green-200 text-green-800';
      default: return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Critical': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'High': return <AlertCircle className="h-4 w-4 text-orange-600" />;
      case 'Medium': return <TrendingUp className="h-4 w-4 text-yellow-600" />;
      case 'Low': return <CheckCircle className="h-4 w-4 text-green-600" />;
      default: return <Settings className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'High':
        return 'bg-orange-100 text-orange-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getReadableRiskFactorLabel = (factorName) => {
    const key = String(factorName || '').toLowerCase();
    if (key === 'timeline pressure') return 'Short time to likely failure';
    if (key === 'maintenance due pressure') return 'Maintenance due checks';
    if (key === 'volume pressure') return 'Higher than fleet average';
    if (key === 'trend pressure') return 'Recent trend direction';
    return factorName || 'Risk signal';
  };

  const getReadableMetricLabel = (metricName) => {
    const key = String(metricName || '').toLowerCase();
    if (key === 'days_until_failure') return 'Estimated failure window';
    if (key === 'retrieval_evidence_count') return 'Similar historical events';
    if (key === 'due_pressure_points') return 'Due checks';
    if (key === 'unit_vs_fleet_ratio') return 'Unit vs fleet event rate';
    if (key === 'trend_slope') return 'Recent trend slope';
    return metricName || 'Metric';
  };

  const getReadableMetricValue = (metricName, rawValue) => {
    const key = String(metricName || '').toLowerCase();
    const numericValue = Number(rawValue);

    if (key === 'days_until_failure' && Number.isFinite(numericValue)) {
      const rounded = Math.max(0, Math.round(numericValue));
      return rounded === 1 ? 'about 1 day' : `about ${rounded} days`;
    }
    if (key === 'retrieval_evidence_count' && Number.isFinite(numericValue)) {
      const rounded = Math.max(0, Math.round(numericValue));
      return rounded === 1 ? '1 event found' : `${rounded} events found`;
    }
    if (key === 'due_pressure_points' && Number.isFinite(numericValue)) {
      const rounded = Math.max(0, Math.round(numericValue));
      return rounded === 1 ? '1 check' : `${rounded} checks`;
    }
    if (key === 'unit_vs_fleet_ratio' && Number.isFinite(numericValue)) {
      const deltaPct = (numericValue - 1) * 100;
      if (Math.abs(deltaPct) < 0.5) {
        return 'at fleet baseline';
      }
      const magnitude = Math.round(Math.abs(deltaPct));
      return deltaPct > 0
        ? `${magnitude}% higher than fleet baseline`
        : `${magnitude}% lower than fleet baseline`;
    }
    if (key === 'trend_slope' && Number.isFinite(numericValue)) {
      if (Math.abs(numericValue) < 0.005) {
        return 'stable at 0.00 events/month';
      }
      const magnitude = Math.abs(numericValue).toFixed(2);
      return numericValue > 0
        ? `rising by ${magnitude} events/month`
        : `falling by ${magnitude} events/month`;
    }
    return String(rawValue);
  };

  const getRiskImpactLevel = (contribution) => {
    const pct = Math.round((Number(contribution) || 0) * 100);
    const isLightTheme = theme === 'light';
    if (pct >= 50) {
      return {
        label: 'Major effect',
        badgeClass: 'bg-red-100 text-red-700 border border-red-200',
        ringColor: '#ef4444',
        cardBackground: isLightTheme
          ? 'linear-gradient(145deg, #fff1f2 0%, #ffffff 72%)'
          : 'linear-gradient(145deg, rgba(239,68,68,0.14) 0%, var(--card-bg) 70%)',
        metricBackground: isLightTheme ? '#ffe4e6' : 'rgba(239,68,68,0.14)',
      };
    }
    if (pct >= 25) {
      return {
        label: 'Moderate effect',
        badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200',
        ringColor: '#f59e0b',
        cardBackground: isLightTheme
          ? 'linear-gradient(145deg, #fffbeb 0%, #ffffff 72%)'
          : 'linear-gradient(145deg, rgba(245,158,11,0.14) 0%, var(--card-bg) 70%)',
        metricBackground: isLightTheme ? '#fef3c7' : 'rgba(245,158,11,0.14)',
      };
    }
    return {
      label: 'Minor effect',
      badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      ringColor: '#10b981',
      cardBackground: isLightTheme
        ? 'linear-gradient(145deg, #ecfdf5 0%, #ffffff 72%)'
        : 'linear-gradient(145deg, rgba(16,185,129,0.14) 0%, var(--card-bg) 70%)',
      metricBackground: isLightTheme ? '#d1fae5' : 'rgba(16,185,129,0.14)',
    };
  };

  const summaryLines = (predictionData?.ai_attention_summary || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const getSummaryValue = (prefix) => {
    const line = summaryLines.find((entry) => entry.toLowerCase().startsWith(prefix.toLowerCase()));
    if (!line) return '';
    return line.replace(new RegExp(`^${prefix}\\s*`, 'i'), '').trim();
  };

  const recurringSignal = getSummaryValue('Recurring signal:');
  const recurringSignalParts = String(recurringSignal || '').split(/Main failure pattern:/i);
  const recurringSignalLead = recurringSignalParts[0]?.trim() || '';
  const recurringFailurePattern = recurringSignalParts.length > 1
    ? recurringSignalParts.slice(1).join('Main failure pattern:').trim()
    : '';
  const hotspotZone = getSummaryValue('Hotspot zone:');
  const likelyGap = getSummaryValue('Likely gap:');
  const nextCheck = getSummaryValue('Next check:');
  const riskLevelLower = (predictionData?.risk_level?.toLowerCase() || 'low');
  const riskTextClass = riskLevelLower === 'critical' || riskLevelLower === 'high'
    ? 'text-red-600'
    : riskLevelLower === 'medium'
      ? 'text-yellow-600'
      : 'text-green-600';

  const explainability = (predictionData?.explainability && typeof predictionData.explainability === 'object')
    ? predictionData.explainability
    : {};
  const riskFactors = Array.isArray(explainability.risk_factor_decomposition)
    ? explainability.risk_factor_decomposition
    : [];
  const confidenceCaveats = Array.isArray(explainability.confidence_caveats)
    ? explainability.confidence_caveats
    : [];
  const actionableRecommendations = Array.isArray(predictionData?.recommendations)
    ? predictionData.recommendations.slice(0, 4)
    : [];
  const nextLikelyIssue = (predictionData?.next_likely_issue && typeof predictionData.next_likely_issue === 'object')
    ? predictionData.next_likely_issue
    : {};
  const alternativeIssues = Array.isArray(nextLikelyIssue.alternatives) ? nextLikelyIssue.alternatives : [];
  const safeRiskFactors = riskFactors.filter((item) => item && typeof item === 'object');
  const visibleRiskFactors = safeRiskFactors.filter((item) => {
    const factorKey = String(item?.factor || '').toLowerCase();
    const metricKey = String(item?.metric || '').toLowerCase();
    return factorKey !== 'prediction signal strength' && metricKey !== 'issue_signal_score';
  });
  const riskCards = visibleRiskFactors.slice(0, 4);
  const safeAlternativeIssues = alternativeIssues.filter((item) => item && typeof item === 'object');
  const actionWindows = ['now', 'this shift', 'next 7 days'];
  const actionWindowLabel = actionWindows[actionWindowIndex % actionWindows.length];
  const actionWindowItems = actionsByWindow[actionWindowLabel] || [];
  const hasActionContent = preventivePlan.length > 0 || actionableRecommendations.length > 0;
  const summaryNextActionText = nextCheck || 'Review actions below.';
  const isQueuedOrLoadingSummary = ['queued', 'loading_summary'].includes(predictionStatus);
  const isLoadingAdvisory = predictionStatus === 'loading_advisory';
  const showFullSkeleton = (!predictionData || predictionData.error) && isQueuedOrLoadingSummary;
  const showPartialAdvisorySkeleton = Boolean(predictionData && !predictionData.error && isLoadingAdvisory);
  const advisoryPending = ['queued', 'loading_advisory', 'retry_wait'].includes(predictionStatus);
  const advisoryFailed = ['error_transient', 'error_terminal'].includes(predictionStatus);

  useEffect(() => {
    if (!isMobile || riskCards.length === 0) {
      setRiskCarouselIndex(0);
      return;
    }
    setRiskCarouselIndex((prev) => Math.min(prev, riskCards.length - 1));
  }, [isMobile, riskCards.length]);

  useEffect(() => {
    if (!isMobile || riskCards.length <= 1) return undefined;
    const timer = setInterval(() => {
      setRiskCarouselIndex((prev) => (prev + 1) % riskCards.length);
    }, 3800);
    return () => clearInterval(timer);
  }, [isMobile, riskCards.length]);

  const getFallbackHealthScore = () => {
    const weightedRisk = Number(explainability?.risk_classification_basis?.inputs?.weighted_risk_score);
    if (!Number.isFinite(weightedRisk)) return null;
    const riskLevel = String(predictionData?.risk_level || '').toLowerCase();
    const rs = Math.max(0, Math.min(1, weightedRisk));

    let score;
    if (riskLevel === 'low') {
      const t = Math.max(0, Math.min(1, rs / 0.40));
      score = 95 - (10 * t);
    } else if (riskLevel === 'medium') {
      const t = Math.max(0, Math.min(1, (rs - 0.40) / 0.20));
      score = 84 - (9 * t);
    } else if (riskLevel === 'high') {
      const t = Math.max(0, Math.min(1, (rs - 0.60) / 0.18));
      score = 74 - (14 * t);
    } else {
      const t = Math.max(0, Math.min(1, (rs - 0.78) / 0.22));
      score = 59 - (19 * t);
    }
    return Math.round(Math.max(40, Math.min(95, score)));
  };
  const displayHealthScore = Number.isFinite(predictionData?.unit_health_score)
    ? Math.round(predictionData.unit_health_score)
    : getFallbackHealthScore();

  const SkeletonLine = ({ widthClass = 'w-full', heightClass = 'h-4' }) => (
    <div className={`${heightClass} ${widthClass} rounded bg-gray-200 animate-pulse`} />
  );
  const SkeletonCard = ({ children, className = '', ...rest }) => (
    <div className={`rounded-lg border border-gray-200 p-4 bg-gray-50 ${className}`} {...rest}>{children}</div>
  );
  const SkeletonDonut = () => (
    <div className="mx-auto h-20 w-20 rounded-full border-[10px] border-gray-200 border-t-blue-200 animate-pulse" />
  );
  const SkeletonBar = () => (
    <div className="rounded-full h-2 bg-gray-200 overflow-hidden">
      <div className="h-full bg-blue-100 rounded-full w-1/3 animate-pulse" />
    </div>
  );

  const renderRiskFactorCard = (factor, idx) => {
    const pct = Math.max(0, Math.min(100, Math.round((Number(factor.contribution) || 0) * 100)));
    const signed = Number(factor.signed_contribution);
    const signedPct = Number.isFinite(signed) ? Math.round(signed * 100) : pct;
    const signPrefix = signedPct > 0 ? '+' : '';
    const impact = getRiskImpactLevel(factor.contribution);
    return (
      <div
        key={`${factor.factor || 'factor'}-${idx}`}
        className="group relative overflow-hidden p-4 rounded-xl border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
        style={{ background: impact.cardBackground, borderColor: 'var(--card-border)' }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="text-base font-semibold leading-5" style={{ color: 'var(--accent-text)' }}>
            {getReadableRiskFactorLabel(factor.factor)}
          </div>
          <span className={`text-sm px-2 py-1 rounded-full ${impact.badgeClass}`}>{impact.label}</span>
        </div>
        <div className="mt-5 flex items-center justify-center">
          <div
            className="h-20 w-20 rounded-full flex items-center justify-center transition-all duration-700"
            style={{
              background: `conic-gradient(${impact.ringColor} ${(sequenceAnimatedIn ? pct : 0) * 3.6}deg, #d1d5db 0deg)`,
              transform: sequenceAnimatedIn ? 'scale(1)' : 'scale(0.85)',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.5), 0 8px 16px rgba(15,23,42,0.08)'
            }}
          >
            <div className="h-14 w-14 rounded-full flex items-center justify-center text-base font-bold" style={{ background: 'var(--card-bg)', color: 'var(--accent-text)' }}>
              {pct}%
            </div>
          </div>
        </div>
        <div className="mt-3 text-sm text-center font-medium" style={{ color: 'var(--accent-text-subtle)' }}>
          Score impact: {signPrefix}{signedPct}%
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${sequenceAnimatedIn ? pct : 0}%`,
              backgroundColor: impact.ringColor
            }}
          />
        </div>
        <div
          className="mt-3 text-sm px-2.5 py-2 rounded-md border text-center"
          style={{
            borderColor: 'var(--card-border)',
            color: 'var(--accent-text)',
            background: impact.metricBackground
          }}
        >
          <span className="font-medium">{getReadableMetricLabel(factor.metric)}:</span> {getReadableMetricValue(factor.metric, factor.value)}
        </div>
      </div>
    );
  };

  const renderFullPredictionSkeleton = () => (
    <div data-testid="prediction-full-skeleton" className="p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="space-y-2">
          <SkeletonLine widthClass="w-40" heightClass="h-6" />
          <SkeletonLine widthClass="w-56" heightClass="h-4" />
        </div>
        <SkeletonLine widthClass="w-24" heightClass="h-8" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <SkeletonCard className="space-y-2">
          <SkeletonLine widthClass="w-28" />
          <SkeletonLine widthClass="w-24" heightClass="h-6" />
        </SkeletonCard>
        <SkeletonCard className="space-y-3">
          <SkeletonLine widthClass="w-36" />
          <SkeletonLine widthClass="w-20" heightClass="h-8" />
          <SkeletonBar />
        </SkeletonCard>
      </div>

      <div className="mb-8 p-4 sm:p-5 rounded-xl border" style={{ borderColor: 'var(--card-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <SkeletonLine widthClass="w-52" />
          <SkeletonLine widthClass="w-28" heightClass="h-7" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <SkeletonCard className="space-y-2">
            <SkeletonLine widthClass="w-28" />
            <SkeletonLine widthClass="w-24" heightClass="h-6" />
          </SkeletonCard>
          <SkeletonCard className="md:col-span-2 space-y-3">
            <SkeletonLine widthClass="w-44" />
            <SkeletonBar />
            <SkeletonBar />
          </SkeletonCard>
        </div>

        <SkeletonLine widthClass="w-36" className="mb-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((idx) => (
            <SkeletonCard key={`risk-skel-${idx}`} className="space-y-3">
              <SkeletonLine widthClass="w-28" />
              <SkeletonDonut />
              <SkeletonLine widthClass="w-full" heightClass="h-3" />
              <SkeletonLine widthClass="w-3/4" heightClass="h-3" />
            </SkeletonCard>
          ))}
        </div>

        <SkeletonCard className="mt-4 space-y-3">
          <SkeletonLine widthClass="w-36" />
          <SkeletonLine />
          <SkeletonLine widthClass="w-11/12" />
          <SkeletonLine widthClass="w-10/12" />
        </SkeletonCard>
      </div>

      <SkeletonCard className="mb-6 space-y-3">
        <SkeletonLine widthClass="w-56" />
        <SkeletonLine />
        <SkeletonLine widthClass="w-11/12" />
        <div className="flex justify-between items-center pt-2">
          <div className="flex gap-2">
            <SkeletonLine widthClass="w-8" heightClass="h-8" />
            <SkeletonLine widthClass="w-8" heightClass="h-8" />
          </div>
          <div className="flex gap-2">
            <SkeletonLine widthClass="w-2.5" heightClass="h-2.5" />
            <SkeletonLine widthClass="w-6" heightClass="h-2.5" />
            <SkeletonLine widthClass="w-2.5" heightClass="h-2.5" />
          </div>
        </div>
      </SkeletonCard>

      <SkeletonCard className="space-y-3">
        <SkeletonLine widthClass="w-44" />
        <SkeletonLine widthClass="w-60" heightClass="h-8" />
        <SkeletonLine />
        <SkeletonLine widthClass="w-5/6" />
      </SkeletonCard>
    </div>
  );

  useEffect(() => {
    setActionWindowIndex(0);
  }, [unitName, predictionData]);

  useEffect(() => {
    setSequenceAnimatedIn(false);
    const timer = setTimeout(() => setSequenceAnimatedIn(true), 80);
    return () => clearTimeout(timer);
  }, [unitName, predictionData?.next_likely_issue, predictionData?.risk_level]);

  useEffect(() => {
    setActiveAltIndex(-1);
  }, [safeAlternativeIssues.length, unitName]);

  if (showFullSkeleton) {
    return renderFullPredictionSkeleton();
  }

  if (!predictionData || (predictionData.error && predictionStatus !== 'error_terminal')) {
    return (
      <div className="p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold" style={{ color: 'var(--accent-text)' }}>{unitName}</h4>
          <button
            onClick={() => onGetPrediction(unitName, false, 'advisory')}
            className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:bg-blue-50 px-3 py-1 rounded-lg transition-colors"
          >
            Get Intelligent Prediction
          </button>
        </div>
      </div>
    );
  }

  if (predictionData?.error) {
    return (
      <div className="p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold" style={{ color: 'var(--accent-text)' }}>{unitName}</h4>
          <button
            onClick={() => onGetPrediction(unitName, false, 'advisory')}
            className="text-blue-600 hover:text-blue-800 text-sm font-semibold hover:bg-blue-50 px-3 py-1 rounded-lg transition-colors"
          >
            Get Intelligent Prediction
          </button>
        </div>

        <div className="text-center text-red-500 text-sm">
          <p>Error loading prediction</p>
          <button
            onClick={() => onGetPrediction(unitName, true, 'advisory')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h4 className="font-semibold text-lg" style={{ color: 'var(--accent-text)' }}>{unitName}</h4>
          <p className="text-base" style={{ color: 'var(--accent-text-subtle)' }}>Intelligent Prediction & Advisory</p>
        </div>
        <div className={`self-start sm:self-auto px-3 py-1 rounded-full text-sm font-medium border ${getRiskColor(predictionData.risk_level)}`}>
          {predictionData.risk_level || 'Low'} Risk
        </div>
      </div>

      {/* Risk Assessment and Confidence */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Risk Level */}
          <div className="text-center p-5 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
            <div className={`text-xl font-bold mb-2 ${riskTextClass}`}>
              {predictionData.risk_level || 'Low'} Risk
            </div>
            <div className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>Risk Assessment</div>
          </div>

          {/* Confidence Score */}
          <div
            className="p-4 sm:p-5 rounded-lg"
            style={{ background: 'color-mix(in srgb, var(--accent-primary) 14%, var(--card-bg))' }}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">Unit Truck Health Score</span>
              </div>
              <div className="text-left sm:text-right">
                <div className="text-3xl font-bold text-blue-600 leading-none">
                  {Number.isFinite(displayHealthScore) ? `${displayHealthScore}%` : 'N/A'}
                </div>
                <div className="text-xs sm:text-sm text-blue-600 mt-1">
                  Calculated from risk drivers
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Sequence Prediction Insights */}
      {predictionData.next_likely_issue && (
        <div className="mb-8 p-4 sm:p-5 rounded-xl border shadow-sm transition-all duration-300 hover:shadow-md" style={{ background: 'linear-gradient(120deg, var(--bg-tertiary) 0%, var(--card-bg) 100%)', borderColor: 'var(--card-border)' }}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
            <h5 className="font-semibold flex items-center text-lg" style={{ color: 'var(--accent-text)' }}>
              <TrendingUp className="h-5 w-5 mr-2 text-blue-700" />
              Sequential Path Prediction
            </h5>
            <div className="px-3 py-1 rounded-full text-xs font-semibold text-blue-700 bg-blue-100 border border-blue-200">
              Next Predicted Event
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
              <div className="text-xs mb-1 uppercase tracking-wider font-bold text-blue-700">Most Likely Next</div>
              <div className="text-lg sm:text-xl font-bold leading-tight" style={{ color: 'var(--accent-text)' }}>{nextLikelyIssue.next_issue}</div>
              <div
                className="mt-2 text-2xl font-black text-blue-700 transition-all duration-500"
                style={{
                  transform: sequenceAnimatedIn ? 'translateY(0px)' : 'translateY(8px)',
                  opacity: sequenceAnimatedIn ? 1 : 0.3
                }}
              >
                {Math.round((Number(nextLikelyIssue.probability) || 0) * 100)}%
              </div>
            </div>

            <div className="md:col-span-2 p-4 rounded-lg border transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
              <div className="text-blue-700 text-xs mb-2 uppercase tracking-wider font-bold">Alternative Path Probabilities</div>
              <div className="space-y-3">
                {safeAlternativeIssues.map((alt, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between gap-2 rounded-md px-2 py-1 transition-all duration-300 ${activeAltIndex === idx ? 'bg-blue-50/80' : 'bg-transparent'}`}
                      onMouseEnter={() => setActiveAltIndex(idx)}
                      onMouseLeave={() => setActiveAltIndex(-1)}
                    >
                      <span className="text-sm font-medium" style={{ color: 'var(--accent-text)' }}>{alt.issue_type}</span>
                      <div className="flex items-center flex-1 mx-1 sm:mx-4">
                      <div className="flex-1 rounded-full h-1.5 overflow-hidden" style={{ background: 'var(--color-border)' }}>
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${activeAltIndex === idx ? 'bg-blue-600' : 'bg-blue-500'}`}
                          style={{
                            width: `${sequenceAnimatedIn ? (Number(alt.probability) || 0) * 100 : 0}%`,
                            boxShadow: activeAltIndex === idx ? '0 0 12px rgba(37,99,235,0.35)' : 'none'
                          }}
                        ></div>
                      </div>
                      <span className={`ml-3 text-xs font-mono transition-colors ${activeAltIndex === idx ? 'text-blue-700' : ''}`} style={{ color: activeAltIndex === idx ? undefined : 'var(--accent-text-subtle)' }}>{Math.round((Number(alt.probability) || 0) * 100)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {visibleRiskFactors.length > 0 ? (
            <div className="mt-4">
              <h5 className="font-semibold mb-3 flex items-center" style={{ color: 'var(--accent-text)' }}>
                <TrendingUp className="h-4 w-4 mr-2 text-blue-600" />
                Risk score drivers
              </h5>
              {isMobile ? (
                <div>
                  <div>
                    {riskCards.length > 0 && renderRiskFactorCard(riskCards[riskCarouselIndex % riskCards.length], riskCarouselIndex)}
                  </div>
                  {riskCards.length > 1 && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setRiskCarouselIndex((prev) => (prev - 1 + riskCards.length) % riskCards.length)}
                          className="p-1.5 rounded-md border hover:bg-gray-50"
                          style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text-subtle)' }}
                          aria-label="Previous risk driver"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setRiskCarouselIndex((prev) => (prev + 1) % riskCards.length)}
                          className="p-1.5 rounded-md border hover:bg-gray-50"
                          style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text-subtle)' }}
                          aria-label="Next risk driver"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {riskCards.map((factor, idx) => (
                          <button
                            key={`risk-dot-${factor.factor || 'factor'}-${idx}`}
                            type="button"
                            onClick={() => setRiskCarouselIndex(idx)}
                            className={`h-2.5 rounded-full transition-all ${idx === (riskCarouselIndex % riskCards.length) ? 'w-6 bg-blue-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400'}`}
                            aria-label={`Go to risk driver ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {riskCards.map((factor, idx) => renderRiskFactorCard(factor, idx))}
                </div>
              )}
            </div>
          ) : showPartialAdvisorySkeleton ? (
            <div data-testid="prediction-risk-skeleton" className="mt-4">
              <h5 className="font-semibold mb-3 flex items-center" style={{ color: 'var(--accent-text)' }}>
                <TrendingUp className="h-4 w-4 mr-2 text-blue-600" />
                Risk score drivers
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((idx) => (
                  <SkeletonCard key={`risk-skel-advisory-${idx}`} className="space-y-3">
                    <SkeletonLine widthClass="w-32" />
                    <SkeletonDonut />
                    <SkeletonLine widthClass="w-full" heightClass="h-3" />
                    <SkeletonLine widthClass="w-3/4" heightClass="h-3" />
                  </SkeletonCard>
                ))}
              </div>
            </div>
          ) : null}
          {predictionData.ai_attention_summary ? (
            <div className="mt-4 p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>
              <div className="text-sm text-blue-700 uppercase tracking-wider font-bold mb-3">
                AI Focus Summary
              </div>
              <div className="space-y-2.5">
                {recurringSignal && (
                  <div className="flex items-start rounded-lg border px-3 py-2.5" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
                    <AlertCircle className="h-4 w-4 mr-2 mt-1 text-yellow-500 flex-shrink-0" />
                    <div>
                      <div className="text-sm uppercase tracking-wide font-semibold" style={{ color: 'var(--accent-text-subtle)' }}>Recurring Signal</div>
                      {recurringSignalLead && (
                        <div className="text-sm leading-6" style={{ color: 'var(--accent-text)' }}>
                          {recurringSignalLead}
                        </div>
                      )}
                      {recurringFailurePattern && (
                        <div className="mt-1 flex items-start text-sm leading-6" style={{ color: 'var(--accent-text)' }}>
                          <AlertTriangle className="h-4 w-4 mr-1.5 mt-1 text-yellow-600 flex-shrink-0" />
                          <span>Main failure pattern: {recurringFailurePattern}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {hotspotZone && (
                  <div className="flex items-start rounded-lg border px-3 py-2.5" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
                    <Package className="h-4 w-4 mr-2 mt-1 text-blue-600 flex-shrink-0" />
                    <div>
                      <div className="text-sm uppercase tracking-wide font-semibold" style={{ color: 'var(--accent-text-subtle)' }}>Hotspot Zone</div>
                      <div className="text-sm leading-6" style={{ color: 'var(--accent-text)' }}>{hotspotZone}</div>
                    </div>
                  </div>
                )}
                {likelyGap && (
                  <div className="flex items-start rounded-lg border px-3 py-2.5" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
                    <Settings className="h-4 w-4 mr-2 mt-1 text-indigo-600 flex-shrink-0" />
                    <div>
                      <div className="text-sm uppercase tracking-wide font-semibold" style={{ color: 'var(--accent-text-subtle)' }}>Likely Gap</div>
                      <div className="text-sm leading-6" style={{ color: 'var(--accent-text)' }}>{likelyGap}</div>
                    </div>
                  </div>
                )}
                {(summaryNextActionText || hasActionContent) && (
                  <div className="flex items-start rounded-lg border px-3 py-2.5" style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}>
                    <CheckCircle className="h-4 w-4 mr-2 mt-1 text-green-600 flex-shrink-0" />
                    <div className="w-full">
                      <div className="text-sm uppercase tracking-wide font-semibold" style={{ color: 'var(--accent-text-subtle)' }}>
                        Next Check - {actionWindowLabel}
                      </div>
                      {(preventivePlan.length > 0 || actionableRecommendations.length > 0) && (
                        <div className="mt-2.5 space-y-2.5">
                          <div>
                            <div className="space-y-2">
                              {actionWindowItems.length > 0 ? actionWindowItems.slice(0, 3).map((action, idx) => (
                                <div key={`${actionWindowLabel}-${idx}`} className="py-1.5 border-b last:border-b-0" style={{ borderColor: 'var(--card-border)' }}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="text-sm font-medium leading-6" style={{ color: 'var(--accent-text)' }}>{action.action}</div>
                                    <span className={`inline-flex text-sm px-2 py-1 rounded-full whitespace-nowrap ${getPriorityBadgeClass(action.priority || 'Medium')}`}>
                                      {action.priority || 'Medium'}
                                    </span>
                                  </div>
                                </div>
                              )) : (
                                <div className="text-sm py-1.5" style={{ color: 'var(--accent-text-subtle)' }}>
                                  No specific action in this window.
                                </div>
                              )}
                            </div>
                            <div className="mt-2.5 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setActionWindowIndex((prev) => (prev - 1 + actionWindows.length) % actionWindows.length)}
                                  className="p-1.5 rounded-md border hover:bg-gray-50"
                                  style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text-subtle)' }}
                                  aria-label="Previous action window"
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setActionWindowIndex((prev) => (prev + 1) % actionWindows.length)}
                                  className="p-1.5 rounded-md border hover:bg-gray-50"
                                  style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text-subtle)' }}
                                  aria-label="Next action window"
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="flex items-center gap-2">
                                {actionWindows.map((windowLabel, idx) => (
                                  <button
                                    key={windowLabel}
                                    type="button"
                                    onClick={() => setActionWindowIndex(idx)}
                                    className={`h-2.5 rounded-full transition-all ${idx === (actionWindowIndex % actionWindows.length) ? 'w-6 bg-green-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400'}`}
                                    aria-label={`Go to ${windowLabel} actions`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          {actionableRecommendations.length > 0 && (
                            <div>
                              <div className="mb-2 text-sm uppercase tracking-wider font-semibold" style={{ color: 'var(--accent-text-subtle)' }}>
                                Actionable Recommendations
                              </div>
                              <div className="space-y-2">
                                {actionableRecommendations.map((rec, index) => (
                                  <div key={index} className={`p-2.5 rounded border ${getPriorityColor(rec.priority)}`}>
                                    <div className="flex items-start space-x-2">
                                      {getPriorityIcon(rec.priority)}
                                      <div className="flex-1">
                                        <div className="font-medium text-sm">{rec.action}</div>
                                        <div className="text-sm opacity-75 mt-1">
                                          <span className="font-medium">Timeline:</span> {rec.timeline} |
                                          <span className="font-medium ml-2">Impact:</span> {rec.impact}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {!hasActionContent && summaryNextActionText && (
                        <div className="mt-2 text-sm leading-6" style={{ color: 'var(--accent-text)' }}>
                          {summaryNextActionText}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : showPartialAdvisorySkeleton ? (
            <SkeletonCard className="mt-4 space-y-3" data-testid="prediction-ai-summary-skeleton">
              <SkeletonLine widthClass="w-36" />
              <SkeletonLine />
              <SkeletonLine widthClass="w-11/12" />
              <SkeletonLine widthClass="w-10/12" />
            </SkeletonCard>
          ) : null}
          <p className="mt-4 text-xs italic" style={{ color: 'var(--accent-text-subtle)' }}>
            * This analysis blends fleet-wide Markov transitions with unit-local sequence context, calibrated for monthly maintenance data.
          </p>
        </div>
      )}

      {(predictionData.detail_level || 'summary').toLowerCase() !== 'advisory' && advisoryPending && (
        <div className="mb-6 p-3 rounded-lg border border-blue-100 bg-blue-50 text-sm text-blue-700">
          Building advisory details for this unit. Summary signals are shown now.
        </div>
      )}
      {(predictionData.detail_level || 'summary').toLowerCase() !== 'advisory' && advisoryFailed && (
        <div className="mb-6 p-3 rounded-lg border border-orange-200 bg-orange-50 text-sm text-orange-800 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>Advisory details timed out in the cloud. Showing summary data only.</span>
          <button
            type="button"
            onClick={() => onGetPrediction(unitName, true, 'advisory')}
            className="self-start sm:self-auto px-3 py-1.5 rounded-md border border-orange-300 bg-white text-orange-800 hover:bg-orange-100 transition-colors"
          >
            Retry advisory
          </button>
        </div>
      )}

      {confidenceCaveats.length > 0 && (
        <div className="mb-6">
          <h5 className="font-semibold mb-2 flex items-center" style={{ color: 'var(--accent-text)' }}>
            <AlertCircle className="h-4 w-4 mr-2 text-yellow-600" />
            Confidence caveats
          </h5>
          <div className="space-y-1">
            {confidenceCaveats.map((caveat, idx) => (
              <div key={`caveat-${idx}`} className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>
                - {caveat}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming services */}
      <div className="mb-6">
        <h5 className="font-semibold mb-3 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Gauge className="h-4 w-4 mr-2 text-purple-500" />
          Upcoming services
        </h5>
        {predictionData.maintenance_checks && !predictionData.maintenance_checks.error ? (
          <div className="rounded-lg p-4 border border-purple-200" style={{ background: 'var(--bg-tertiary)' }}>
            {/* Current SMU and Status */}
            <div className="mb-4 p-3 rounded-lg border border-purple-200" style={{ background: 'var(--card-bg)' }}>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
                <div>
                  <div className="text-sm text-gray-600">Current Operating Hours (SMU)</div>
                  <div className="text-2xl font-bold text-purple-700">
                    {predictionData.maintenance_checks.current_smu?.toLocaleString()} hrs
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${predictionData.maintenance_checks.maintenance_status?.includes('Critical') ? 'bg-red-100 text-red-700' :
                  predictionData.maintenance_checks.maintenance_status?.includes('Attention') ? 'bg-orange-100 text-orange-700' :
                    predictionData.maintenance_checks.maintenance_status?.includes('Due Soon') ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                  }`}>
                  {predictionData.maintenance_checks.maintenance_status}
                </div>
              </div>
            </div>

            {/* Next Major Service */}
            {predictionData.maintenance_checks.next_major_service && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-blue-900">Next Major Service</span>
                  <span className="text-sm bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                    {predictionData.maintenance_checks.next_major_service.smu_remaining} hrs remaining
                  </span>
                </div>
                <div className="text-sm text-blue-800 mb-2">
                  {predictionData.maintenance_checks.next_major_service.description}
                </div>
                <div className="text-sm text-blue-700">
                  Due at: {predictionData.maintenance_checks.next_major_service.due_at_smu?.toLocaleString()} hrs
                </div>
              </div>
            )}

            {/* Next services */}
            {predictionData.maintenance_checks.upcoming_maintenance?.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
                  <span>Next services</span>
                  <span className="text-sm bg-purple-200 text-purple-700 px-2 py-1 rounded-full">
                    {predictionData.maintenance_checks.total_upcoming} items
                  </span>
                </div>
                <div className="space-y-2">
                  {(showAllUpcoming
                    ? predictionData.maintenance_checks.upcoming_maintenance
                    : predictionData.maintenance_checks.upcoming_maintenance.slice(0, 5)
                  ).map((item, index) => (
                    <div key={index} className="p-2 rounded border border-purple-200 hover:border-purple-300 transition-colors" style={{ background: 'var(--card-bg)' }}>
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-1">
                          <span className="text-sm font-medium leading-5 text-gray-700">{item.description}</span>
                        <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${item.urgency === 'Critical' ? 'bg-red-100 text-red-700' :
                          item.urgency === 'High' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                          {item.urgency}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Due: {item.due_at_smu?.toLocaleString()} hrs ({item.smu_remaining} hrs remaining)
                      </div>
                      <div className="text-sm text-gray-500 mt-1">{item.category}</div>
                    </div>
                  ))}
                  {predictionData.maintenance_checks.upcoming_maintenance.length > 5 && (
                    <button
                      onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                      className="w-full text-xs text-center text-purple-600 hover:text-purple-800 py-2 hover:bg-purple-50 rounded transition-colors flex items-center justify-center gap-1"
                    >
                      {showAllUpcoming ? (
                        <>
                          <ChevronUp className="h-3 w-3" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3" />
                          Show All ({predictionData.maintenance_checks.upcoming_maintenance.length - 5} more items)
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Overdue Checks */}
            {predictionData.maintenance_checks.overdue_checks?.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-semibold text-red-700 mb-2 flex items-center justify-between">
                  <span className="flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Overdue Checks
                  </span>
                  <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded-full">
                    {predictionData.maintenance_checks.total_overdue} items
                  </span>
                </div>
                <div className="space-y-2">
                  {(showAllOverdue
                    ? predictionData.maintenance_checks.overdue_checks
                    : predictionData.maintenance_checks.overdue_checks.slice(0, 5)
                  ).map((item, index) => (
                    <div key={index} className="p-2 bg-red-50 rounded border border-red-200">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-1">
                          <span className="text-sm font-medium leading-5 text-red-900">{item.description}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.severity === 'Critical' ? 'bg-red-200 text-red-900' :
                          item.severity === 'High' ? 'bg-orange-200 text-orange-900' :
                            'bg-yellow-200 text-yellow-900'
                          }`}>
                          {item.severity}
                        </span>
                      </div>
                      <div className="text-xs text-red-800">
                        Was due: {item.was_due_at_smu?.toLocaleString()} hrs ({item.smu_overdue} hrs overdue)
                      </div>
                      <div className="text-xs text-red-700 mt-1">{item.category}</div>
                    </div>
                  ))}
                  {predictionData.maintenance_checks.overdue_checks.length > 5 && (
                    <button
                      onClick={() => setShowAllOverdue(!showAllOverdue)}
                      className="w-full text-xs text-center text-red-600 hover:text-red-800 py-2 hover:bg-red-50 rounded transition-colors flex items-center justify-center gap-1"
                    >
                      {showAllOverdue ? (
                        <>
                          <ChevronUp className="h-3 w-3" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3" />
                          Show All ({predictionData.maintenance_checks.overdue_checks.length - 5} more overdue items)
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Checks Made (Recent Maintenance) */}
            {predictionData.maintenance_checks.recent_maintenance?.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-semibold text-green-700 mb-2 flex items-center justify-between">
                  <span className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Checks Made
                  </span>
                  <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full">
                    {predictionData.maintenance_checks.recent_maintenance.length} completed
                  </span>
                </div>
                <div className="space-y-2">
                  {(showAllChecksMade
                    ? predictionData.maintenance_checks.recent_maintenance
                    : predictionData.maintenance_checks.recent_maintenance.slice(0, 5)
                  ).map((item, index) => (
                    <div key={index} className="p-2 rounded border border-green-200 hover:border-green-300 transition-colors" style={{ background: 'var(--card-bg)' }}>
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-1">
                          <span className="text-sm font-medium leading-5 text-gray-700">{item.description}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                          ✓ Complete
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        At: {item.smu?.toLocaleString()} hrs on {item.date}
                      </div>
                    </div>
                  ))}
                  {predictionData.maintenance_checks.recent_maintenance.length > 5 && (
                    <button
                      onClick={() => setShowAllChecksMade(!showAllChecksMade)}
                      className="w-full text-xs text-center text-green-600 hover:text-green-800 py-2 hover:bg-green-50 rounded transition-colors flex items-center justify-center gap-1"
                    >
                      {showAllChecksMade ? (
                        <>
                          <ChevronUp className="h-3 w-3" />
                          Show Less
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3 w-3" />
                          Show All ({predictionData.maintenance_checks.recent_maintenance.length - 5} more completed checks)
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* No maintenance due message */}
            {(!predictionData.maintenance_checks.upcoming_maintenance || predictionData.maintenance_checks.upcoming_maintenance.length === 0) &&
              (!predictionData.maintenance_checks.overdue_checks || predictionData.maintenance_checks.overdue_checks.length === 0) && (
                <div className="text-center py-4">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-green-700 font-medium">All maintenance up to date!</p>
                  <p className="text-xs text-green-600 mt-1">No upcoming or overdue checks</p>
                </div>
              )}
          </div>
        ) : showPartialAdvisorySkeleton ? (
          <SkeletonCard className="space-y-3" data-testid="prediction-checks-skeleton">
            <SkeletonLine widthClass="w-56" />
            <SkeletonLine widthClass="w-48" heightClass="h-8" />
            <SkeletonLine />
            <SkeletonLine widthClass="w-5/6" />
          </SkeletonCard>
        ) : (
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
            <div className="text-center py-3">
              <p className="text-sm text-purple-600 font-medium">Mileage-based maintenance schedule</p>
              <p className="text-xs text-purple-500 mt-1">
                {predictionData.maintenance_checks?.error || 'Loading maintenance data...'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Inventory Status */}
      <div className="mb-6">
        <h5 className="font-semibold mb-3 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Package className="h-4 w-4 mr-2 text-indigo-500" />
          Inventory Status
        </h5>
        <div className="rounded-lg p-3 border border-indigo-200" style={{ background: 'linear-gradient(to right, var(--bg-tertiary), var(--card-bg))' }}>
          <div className="text-center py-3">
            <p className="text-sm font-medium" style={{ color: 'var(--accent-primary)' }}>Parts inventory tracking</p>
            <p className="text-xs mt-1" style={{ color: 'var(--accent-text-subtle)' }}>Feature not available at this moment</p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default IntelligentUnitPrediction;

